import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { InformationCircleIcon } from '@heroicons/react/20/solid'

export interface TooltipProps {
  /** The content shown inside the tooltip panel. Accepts plain text or rich nodes. */
  content: ReactNode
  /**
   * Custom trigger element. When omitted, a default info-circle icon button is rendered.
   * Whatever is passed is wrapped in the PopoverButton, so it toggles the panel on click/tap.
   */
  children?: ReactNode
  /**
   * Accessible label for the default icon trigger. Pass a translated string to override
   * the built-in default (which is itself translated via i18n).
   */
  triggerLabel?: string
  /**
   * Panel color scheme. `dark` (default) is a dark surface with light text; `light` is a
   * white surface with dark text for use on dark/colored backgrounds (e.g. the invitation page).
   */
  variant?: 'dark' | 'light'
  /** Extra layout classes for the default trigger button. */
  triggerStyle?: string
}

/**
 * Reusable click/tap-toggle tooltip built on Headless UI's Popover so it works
 * identically on desktop and touch devices (hover-only tooltips can't open on mobile).
 *
 * Use standalone next to any element, or pass via the `tooltip` prop on form inputs.
 * The `content` must be pre-translated by the caller; the default trigger's aria-label
 * is translated internally so the component is accessible without extra wiring.
 */
export default function Tooltip({ content, children, triggerLabel, variant = 'dark', triggerStyle }: TooltipProps) {
  const { t } = useTranslation()

  const defaultTriggerStyles =
    'inline-flex items-center justify-center text-tertiary-txt transition-colors hover:text-secondary-txt focus:outline-none focus:ring-2 focus:ring-cta rounded-full'

  const panelVariant =
    variant === 'light' ? 'bg-alt text-alt-txt ring-1 ring-alt-txt/10' : 'bg-tooltip text-tooltip-txt'

  return (
    <Popover className='relative inline-flex'>
      <PopoverButton
        aria-label={triggerLabel ?? t('content.components.tooltip.trigger_label')}
        className={children ? (triggerStyle ?? '') : `${defaultTriggerStyles} ${triggerStyle ?? ''}`}
      >
        {children ?? (
          <InformationCircleIcon
            aria-hidden='true'
            className={`h-4 w-4 ${variant === 'light' ? 'text-alt hover:text-alt/70' : 'text-cta hover:text-cta/70'}`}
          />
        )}
      </PopoverButton>

      <PopoverPanel
        anchor='top'
        transition
        className={`z-50 max-w-xs rounded-md ${panelVariant} px-3 py-2 text-xs font-extralight shadow-lg font-sans transition-opacity data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in [--anchor-gap:6px]`}
      >
        {content}
      </PopoverPanel>
    </Popover>
  )
}
